import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * The two brands this one app wears.
 *
 * D-I7: the customer screens are the *participating website's* UI and the operator screens are
 * *PaymentFlow's* console. Making them look identical would quietly undo the architecture the
 * split exists to demonstrate — a reviewer should be able to tell, from a screenshot, which
 * system's product they are looking at.
 */

/** The PaymentFlow mark, used across the operator console. */
export function PaymentFlowMark({
  className,
  size = 28,
}: {
  /** Additional classes. */
  className?: string | undefined;
  /** Edge length in pixels. */
  size?: number | undefined;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={cn('shrink-0', className)}
      role="img"
      aria-label={t('layout.brand.paymentFlowAriaLabel')}
    >
      <rect width="32" height="32" rx="8" className="fill-primary" />
      <path d="M9 23V9h6.6a4.7 4.7 0 0 1 0 9.4H13V23z" className="fill-primary-foreground" />
      <circle cx="21.5" cy="21.5" r="2.5" className="fill-primary-foreground/70" />
    </svg>
  );
}

/** The participating website's mark, used across the customer area. */
export function WebsiteMark({
  className,
  size = 28,
}: {
  /** Additional classes. */
  className?: string | undefined;
  /** Edge length in pixels. */
  size?: number | undefined;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className={cn('shrink-0', className)}
      role="img"
      aria-label={t('layout.brand.websiteAriaLabel')}
    >
      <rect width="32" height="32" rx="10" fill="#0f766e" />
      <path
        d="M10 21c3.5 0 3.5-10 7-10s3.5 10 7 10"
        stroke="#5eead4"
        strokeWidth="2.4"
        fill="none"
        strokeLinecap="round"
      />
      <circle cx="10" cy="21" r="2.2" fill="#5eead4" />
    </svg>
  );
}

/** Lockup for the operator console. */
export function PaymentFlowLockup({ className }: { className?: string | undefined }): ReactNode {
  const { t } = useTranslation();
  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      <PaymentFlowMark size={26} />
      <span className="min-w-0 leading-tight">
        <span className="block truncate text-sm font-semibold tracking-tight">
          {t('layout.brand.paymentFlowName')}
        </span>
        <span className="block truncate text-2xs text-sidebar-muted">
          {t('layout.brand.operatorConsoleTagline')}
        </span>
      </span>
    </span>
  );
}

/** Lockup for the participating website. */
export function WebsiteLockup({ className }: { className?: string | undefined }): ReactNode {
  const { t } = useTranslation();
  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      <WebsiteMark size={26} />
      <span className="min-w-0 leading-tight">
        <span className="block truncate text-sm font-semibold tracking-tight">
          {t('layout.brand.websiteName')}
        </span>
        <span className="block truncate text-2xs text-muted-foreground">
          {t('layout.brand.cashierTagline')}
        </span>
      </span>
    </span>
  );
}
